import { Inject, Injectable } from '@angular/core';
import * as fromRoot from '@core/store/reducers';
import { select, Store } from '@ngrx/store';
import { roleLandingMap } from '@shared/constants/role-landing.map';
import { Feature } from '@shared/enums/feature.enum';
import { RouteSegment } from '@shared/enums/route-segment.enum';
import { Profile } from '@shared/interfaces/profile.interface';
import { ReportType, UserRole } from '@wtax/data-angular';
import isNil from 'lodash/isNil';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { Config, CONFIG_TOKEN } from 'wtax-config';

/**
 * Centralises the logic for determining where a user should land after login,
 * account setup completion, or any other redirect-to-home scenario.
 *
 * When `display_client_action === false` the default landing for WM / FM / BO
 * roles (which is the Client Action tab) is skipped and the first other
 * visible tab is used instead: Reporting → Insights → Payments.
 */
@Injectable({ providedIn: 'root' })
export class LandingRouteService {
  constructor(
    @Inject(CONFIG_TOKEN) private readonly config: Config,
    private readonly store$: Store<fromRoot.State>
  ) {}

  /**
   * Emits once with the correct landing route segments for the current user,
   * taking `display_client_action` into account.
   */
  public getLandingRoute$(): Observable<string[]> {
    return this.store$.pipe(
      select(fromRoot.selectUserProfile),
      first((profile) => !isNil(profile?.role)),
      map((profile) => this.getLandingRouteForProfile(profile))
    );
  }

  /**
   * Synchronously resolves the landing route for a given profile snapshot.
   * Useful in effects where you already have the profile value.
   */
  public getLandingRouteForProfile(profile: Profile): string[] {
    const role = profile.role;
    const defaultLanding = roleLandingMap.get(role);

    if (isNil(defaultLanding)) {
      return [RouteSegment.Root];
    }

    const isClientActionRole = role === UserRole.WEALTH_MANAGER || role === UserRole.FUND_MANAGER || role === UserRole.BENEFICIAL_OWNER;

    if (isClientActionRole && profile.display_client_action === false) {
      return this.getFirstVisibleFallbackRoute(profile.report_type);
    }

    return defaultLanding;
  }

  /**
   * Resolves the first accessible route for roles where Client Action is hidden,
   * following the same priority as the header menu:
   * Reporting → Insights → Payments.
   */
  private getFirstVisibleFallbackRoute(reportType: ReportType): string[] {
    if (!this.config.restrictedUI.includes(Feature.ReportingPage) && reportType === ReportType.POWERBI_REPORTING) {
      return [RouteSegment.Root, RouteSegment.Reporting];
    }

    if (!this.config.restrictedUI.includes(Feature.InsightsPage) && reportType === ReportType.POWERBI_REPORTING) {
      return [RouteSegment.Root, RouteSegment.Insights];
    }

    // Payments is always visible for WM / FM / BO
    return [RouteSegment.Root, RouteSegment.Payments];
  }
}
